import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import LocationOptions, { useAllLocations } from '../components/LocationOptions';

const STATUSES = ['All', 'submitted', 'reviewed', 'archived'];

const STATUS_STYLE = {
  submitted: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', label: '⏳ Submitted' },
  reviewed:  { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: '✅ Reviewed'  },
  archived:  { bg: 'rgba(100,116,139,0.12)',color: '#94a3b8', label: '📁 Archived'  },
};

export default function InspectionList() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [inspections, setInspections] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [stats,       setStats]       = useState(null);
  const locationGroups = useAllLocations();   // CarSaloon sites + inspection locations + dealerships

  const [filters, setFilters] = useState({
    site: 'All Sites', status: 'All', from: '', to: '', page: 1,
  });
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // ── Fetch inspections ──
  const fetchInspections = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: filters.page, limit: 15 };
      if (filters.site   !== 'All Sites') params.site   = filters.site;
      if (filters.status !== 'All')       params.status = filters.status;
      if (filters.from)                   params.from   = filters.from;
      if (filters.to)                     params.to     = filters.to;

      const { data } = await api.get('/inspections', { params });
      setInspections(data.inspections);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load inspections');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ── Fetch stats (owner only) ──
  const fetchStats = useCallback(async () => {
    if (user?.role !== 'owner') return;
    try {
      const { data } = await api.get('/inspections/stats');
      setStats(data.stats);
    } catch {}
  }, [user]);

  useEffect(() => { fetchInspections(); }, [fetchInspections]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val, page: 1 }));

  // ── Score helper ──
  const calcScore = (insp) => {
    let done = 0, total = 0;
    const sections = [
      { data: insp.staffPresentation, items: ['uniforms','cleaning','punctuality','jobPerformance','fillingBottleEndOfDay','cleaningSiteAfterWork'] },
      { data: insp.siteCleaning,      items: ['floorClean','washpadClean','binsEmpty','bottlesLabeled','highPressureToolsWorking','wiresJointsCovered','trolleysClean','ragsClean','usedGlovesInBin'] },
      { data: insp.quality,           items: ['carsDeliveredAsPerStandard','carsDeliveredOnTime','carsHandledWithCare','carDamagePicsRecorded','carVideosRecordedBeforeWashing','carsParkedInDesignatedArea','carKeysReturnedAsDirected','carsWrittenOnSheetWithTimings'] },
      { data: insp.managerFeedback,   items: ['quality','cleaning','sitePresentation','communication','invoicesOnTime','staffPunctuality'] },
    ];
    sections.forEach(s => {
      s.items.forEach(k => {
        total++;
        if (s.data?.[k]?.rating && s.data[k].rating !== null) done++;
      });
    });
    return total > 0 ? Math.round((done / total) * 100) : 0;
  };

  return (
    <div style={{ minHeight: '100vh', background:'var(--bg)', fontFamily: "'Inter', sans-serif" }}>

      {/* Header */}
      <div style={{ background: '#ffffff', borderBottom: '1.5px solid var(--border)', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'var(--bg)', border: '1px solid var(--border-md)', color: 'var(--muted)', borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', fontSize: '.8rem' }}>← Back</button>
        <div>
          <div style={{ color: 'var(--text)', fontWeight: 700, fontSize: '1rem' }}>Site Inspections</div>
          <div style={{ color: 'var(--blue)', fontSize: '.7rem' }}>Car Saloon</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/inspection/new')} style={{ background: 'var(--cyan)', border: 'none', borderRadius: '8px', padding: '8px 16px', color: '#000', fontWeight: 700, fontSize: '.8rem', cursor: 'pointer', fontFamily: 'inherit' }}>
            + New Inspection
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '22px 20px' }}>

        {/* Stats (owner only) — clickable filters */}
        {user?.role === 'owner' && stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '14px', marginBottom: '22px' }}>
            {[
              { icon: '📋', val: stats.total,      label: 'Total',          color: 'var(--blue)',      onClick: () => setFilters({ site:'All Sites', status:'All', from:'', to:'', page:1 }) },
              { icon: '📅', val: stats.todayCount,  label: 'Today',          color: 'var(--green)', onClick: () => setFilters(f => ({ ...f, status:'All', from: new Date().toISOString().split('T')[0], to: new Date().toISOString().split('T')[0], page:1 })) },
              { icon: '⏳', val: stats.byStatus?.find(s=>s._id==='submitted')?.count||0, label: 'To Be Reviewed', color: 'var(--amber)',  onClick: () => setFilter('status', 'submitted') },
              { icon: '✅', val: stats.byStatus?.find(s=>s._id==='reviewed')?.count||0,  label: 'Reviewed',      color: 'var(--green)', onClick: () => setFilter('status', 'reviewed') },
            ].map(s => {
              const active = (s.label === 'To Be Reviewed' && filters.status === 'submitted') || (s.label === 'Reviewed' && filters.status === 'reviewed');
              return (
                <div key={s.label} onClick={s.onClick}
                  style={{ background: active ? `${s.color}12` : '#ffffff', border: `1.5px solid ${active ? s.color : 'var(--border-md)'}`, borderRadius: '12px', padding: '14px 16px', cursor: 'pointer', transition: 'all .15s' }}
                  onMouseEnter={e => { if(!active) e.currentTarget.style.borderColor = s.color; }}
                  onMouseLeave={e => { if(!active) e.currentTarget.style.borderColor = 'var(--border-md)'; }}>
                  <div style={{ fontSize: '1.3rem', marginBottom: '6px' }}>{s.icon}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>{s.val}</div>
                  <div style={{ fontSize: '.7rem', color: 'var(--muted)', marginTop: '3px' }}>{s.label} {s.label !== 'Total' && s.label !== 'Today' && <span style={{ color: s.color }}>· tap to view</span>}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', background: '#ffffff', border: '1px solid var(--border-md)', borderRadius: '12px', padding: '14px 16px' }}>
          {user?.role === 'owner' && (
            <select value={filters.site} onChange={e => setFilter('site', e.target.value)} style={selStyle}>
              <option value="All Sites">All Sites</option>
              <LocationOptions groups={locationGroups}/>
            </select>
          )}
          <select value={filters.status} onChange={e => setFilter('status', e.target.value)} style={selStyle}>
            {STATUSES.map(s => <option key={s} value={s}>{s === 'All' ? 'All Status' : s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
          </select>
          <input type="date" value={filters.from} onChange={e => setFilter('from', e.target.value)} style={{ ...selStyle, color: filters.from ? '#fff' : 'var(--muted)' }} placeholder="From"/>
          <input type="date" value={filters.to} onChange={e => setFilter('to', e.target.value)} style={{ ...selStyle, color: filters.to ? '#fff' : 'var(--muted)' }} placeholder="To"/>
          <button onClick={() => setFilters({ site: 'All Sites', status: 'All', from: '', to: '', page: 1 })}
            style={{ background: 'transparent', border: '1px solid var(--border-md)', color: 'var(--muted)', borderRadius: '8px', padding: '7px 14px', cursor: 'pointer', fontSize: '.78rem', fontFamily: 'inherit' }}>
            Clear
          </button>
          <div style={{ marginLeft: 'auto', fontSize: '.78rem', color: 'var(--muted)', alignSelf: 'center' }}>
            {total} record{total !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Error */}
        {error && <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '9px', padding: '12px 16px', color: 'var(--red)', marginBottom: '16px', fontSize: '.84rem' }}>{error}</div>}

        {/* Table */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-md)', borderRadius: '14px', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: 'var(--muted)' }}>Loading inspections...</div>
          ) : inspections.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📋</div>
              <div style={{ color: 'var(--text)', fontWeight: 600, marginBottom: '6px' }}>No inspections found</div>
              <div style={{ color: 'var(--muted)', fontSize: '.84rem' }}>Try adjusting your filters or submit a new inspection</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '750px' }}>
                <thead>
                  <tr>
                    {['Manager', 'Site', 'Date', 'Completion', 'Photos', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--muted)', borderBottom: '1px solid var(--border-md)', background: 'rgba(255,255,255,0.02)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {inspections.map(insp => {
                    const score = calcScore(insp);
                    const st    = STATUS_STYLE[insp.status] || STATUS_STYLE.submitted;
                    const totalPhotos = [insp.staffPresentation, insp.siteCleaning, insp.quality, insp.managerFeedback, insp.suggestions].reduce((a, s) => a + (s?.photos?.length || 0), 0);
                    return (
                      <tr key={insp._id} style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/inspection/${insp._id}`)}
                        onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '.85rem' }}>{insp.managerName}</div>
                          <div style={{ color: 'var(--muted)', fontSize: '.7rem', marginTop: '1px' }}>{insp.managerEmail}</div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '.8rem', color: 'var(--blue)' }}>📍 {insp.site?.replace('Hertz ', '')}</span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontSize: '.8rem', color: 'var(--text)', fontFamily: "'JetBrains Mono', monospace" }}>
                            {new Date(insp.submittedAt).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </div>
                          <div style={{ fontSize: '.7rem', color: 'var(--muted)' }}>
                            {new Date(insp.submittedAt).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '5px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden', minWidth: '60px' }}>
                              <div style={{ height: '100%', width: `${score}%`, background: score >= 80 ? 'var(--green)' : score >= 50 ? 'var(--amber)' : 'var(--red)', borderRadius: '3px', transition: 'width .4s' }}/>
                            </div>
                            <span style={{ fontSize: '.72rem', fontWeight: 700, color: score >= 80 ? 'var(--green)' : score >= 50 ? 'var(--amber)' : 'var(--red)', fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'nowrap' }}>{score}%</span>
                          </div>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: '.8rem', color: totalPhotos > 0 ? 'var(--cyan)' : 'var(--muted)' }}>
                            📸 {totalPhotos}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: '20px', fontSize: '.7rem', fontWeight: 700 }}>{st.label}</span>
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={e => { e.stopPropagation(); navigate(`/inspection/${insp._id}`); }}
                              style={actBtnStyle}>View</button>
                            {user?.role === 'owner' && insp.status === 'submitted' && (
                              <button onClick={e => { e.stopPropagation(); navigate(`/inspection/${insp._id}`); }}
                                style={{ ...actBtnStyle, borderColor: 'rgba(34,197,94,.3)', color: 'var(--green)' }}>Review</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
            <button disabled={filters.page <= 1} onClick={() => setFilter('page', filters.page - 1)}
              style={{ ...actBtnStyle, opacity: filters.page <= 1 ? .4 : 1 }}>← Prev</button>
            <span style={{ color: 'var(--muted)', fontSize: '.82rem', alignSelf: 'center', fontFamily: "'JetBrains Mono', monospace" }}>
              {filters.page} / {pages}
            </span>
            <button disabled={filters.page >= pages} onClick={() => setFilter('page', filters.page + 1)}
              style={{ ...actBtnStyle, opacity: filters.page >= pages ? .4 : 1 }}>Next →</button>
          </div>
        )}

      </div>
    </div>
  );
}

const selStyle = { background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: '8px', padding: '7px 12px', color: 'var(--text)', fontFamily: 'inherit', fontSize: '.8rem', outline: 'none' };
const tdStyle  = { padding: '11px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'middle' };
const actBtnStyle = { background: 'transparent', border: '1px solid var(--border-md)', color: 'var(--muted)', borderRadius: '6px', padding: '4px 10px', fontSize: '.72rem', cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' };
